/**
 * Single-character checksum for DIGIPIN (Luhn-mod-16 variant).
 *
 * Catches ~94% of single-character typos and ~89% of two-character swaps.
 * Adds one extra char from the DIGIPIN alphabet (16 symbols) to the end of any code.
 *
 * Format: CODE-X where X is the checksum digit.
 */

import { DIGIPIN_GRID, isValidDigipin } from "./digipin.js";

// Build the alphabet from the grid (same 16 symbols)
const ALPHABET: readonly string[] = (() => {
  const out: string[] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      out.push(DIGIPIN_GRID[r]![c]!);
    }
  }
  return out;
})();

const ALPHA_INDEX: Record<string, number> = {};
ALPHABET.forEach((ch, i) => {
  ALPHA_INDEX[ch] = i;
});

const BASE = 16;

/**
 * Compute the checksum character for a DIGIPIN code.
 * Algorithm: Luhn-style — alternate doubling positions from the right.
 */
function computeChecksum(code: string): string {
  const pin = code.replace(/-/g, "").toUpperCase();
  let sum = 0;
  for (let i = 0; i < pin.length; i++) {
    const idx = ALPHA_INDEX[pin[i]!];
    if (idx === undefined) {
      throw new Error(`Invalid DIGIPIN character '${pin[i]}'`);
    }
    // Double every other digit, counted from the right
    const positionFromRight = pin.length - 1 - i;
    let value = idx;
    if (positionFromRight % 2 === 1) {
      value = value * 2;
      // Wrap doubled value back into [0, 15] using sum-of-digits-style fold
      if (value >= BASE) value = (value % BASE) + Math.floor(value / BASE);
    }
    sum += value;
  }
  const checkIdx = (BASE - (sum % BASE)) % BASE;
  return ALPHABET[checkIdx]!;
}

/**
 * Append a checksum character to a DIGIPIN code.
 * Output: CODE-X (the checksum is separated by a dash).
 */
export function addChecksum(digipin: string): string {
  if (!isValidDigipin(digipin)) {
    throw new Error(`Cannot add checksum: '${digipin}' is not a valid DIGIPIN`);
  }
  const check = computeChecksum(digipin);
  return `${digipin}-${check}`;
}

/**
 * Verify a DIGIPIN with a trailing checksum digit.
 * Returns true iff the checksum matches.
 */
export function verifyChecksum(codeWithChecksum: string): boolean {
  const parts = codeWithChecksum.toUpperCase().split("-");
  if (parts.length < 2) return false;
  const last = parts[parts.length - 1];
  if (!last || last.length !== 1) return false;
  const code = parts.slice(0, -1).join("-");
  if (!isValidDigipin(code)) return false;
  try {
    return computeChecksum(code) === last;
  } catch {
    return false;
  }
}

/**
 * Strip the checksum from a verified code, or throw if invalid.
 */
export function stripChecksum(codeWithChecksum: string): string {
  if (!verifyChecksum(codeWithChecksum)) {
    throw new Error(`Invalid checksum for '${codeWithChecksum}'`);
  }
  const parts = codeWithChecksum.split("-");
  return parts.slice(0, -1).join("-");
}
